import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  TrendingUp,
  Users,
  AlertTriangle,
  Wrench,
  Receipt,
  LifeBuoy,
  XCircle,
  Activity,
  Heart,
  Sparkles,
  Hammer,
} from 'lucide-react';
import { UniversalPage, Card, NotificationCard, StatCard } from '@afrilink/ui';
import {
  adminReportsService,
  type DashboardMetrics,
  type Phase10Metrics,
} from '@/domains/reports/services/adminReportsService';
import { formatZAR } from '@/shared/utils/format';

export default function ReportsOverviewPage() {
  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [phase10, setPhase10] = useState<Phase10Metrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phase10Error, setPhase10Error] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminReportsService
      .getDashboardMetrics()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    // Phase 10 metrics fetched in parallel — failures here are isolated from
    // the core dashboard so a hiccup in the Phase 10 views doesn't blank the
    // whole page.
    adminReportsService
      .getPhase10Metrics()
      .then((p) => {
        if (!cancelled) setPhase10(p);
      })
      .catch((e: unknown) => {
        if (!cancelled) setPhase10Error(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <UniversalPage
      title="Reports"
      breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Reports' }]}
    >
      {error && <NotificationCard tone="danger" title="Could not load metrics" body={error} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="MRR"
          value={data ? formatZAR(data.mrr) : '…'}
          icon={TrendingUp}
          tone="success"
          sub="Monthly recurring revenue, normalised across billing cycles."
          interactive={false}
        />
        <StatCard
          label="Active customers"
          value={data ? data.active_customers.toString() : '…'}
          icon={Users}
          tone="primary"
          sub={data ? `${data.lead_customers} lead · ${data.suspended_customers} suspended · ${data.churned_customers} churned` : undefined}
          interactive={false}
        />
        {/* Headline is the TRUE outstanding figure (overdue + not yet due); the
            sub-line splits it. Previously the headline was that same total
            under a warning tone labelled from `overdue_total`, so every issued
            invoice read as arrears. The tone now fires only on real lateness. */}
        <StatCard
          label="Outstanding total"
          value={data ? formatZAR(data.outstanding_total) : '…'}
          icon={AlertTriangle}
          tone={data && data.overdue_total > 0 ? 'warning' : 'muted'}
          sub={data ? `${formatZAR(data.overdue_total)} overdue · ${formatZAR(data.due_total)} not yet due` : undefined}
          link={{ to: '/admin/billing', label: 'Open billing' }}
          interactive={false}
        />
        <StatCard
          label="Open tickets"
          value={data ? data.open_tickets.toString() : '…'}
          icon={LifeBuoy}
          tone={data && data.open_tickets > 0 ? 'info' : 'muted'}
          link={{ to: '/admin/support', label: 'Open inbox' }}
          interactive={false}
        />
        <StatCard
          label="Active jobs"
          value={data ? data.active_jobs.toString() : '…'}
          icon={Activity}
          tone="primary"
          sub="Pending or running provisioning jobs."
          link={{ to: '/admin/ops/provisioning', label: 'Open queue' }}
          interactive={false}
        />
        <StatCard
          label="Failed jobs"
          value={data ? data.failed_jobs.toString() : '…'}
          icon={XCircle}
          tone={data && data.failed_jobs > 0 ? 'danger' : 'muted'}
          sub="Jobs in failed_terminal or failed_retryable."
          link={{ to: '/admin/reports/provisioning', label: 'Drill down' }}
          interactive={false}
        />
        <StatCard
          label="Suspended customers"
          value={data ? data.suspended_customers.toString() : '…'}
          icon={Wrench}
          tone={data && data.suspended_customers > 0 ? 'warning' : 'muted'}
          interactive={false}
        />
        <StatCard
          label="Invoices outstanding"
          value={data ? (data.invoices_unpaid + data.invoices_in_grace + data.invoices_suspended).toString() : '…'}
          icon={Receipt}
          tone={data && (data.invoices_unpaid + data.invoices_in_grace + data.invoices_suspended) > 0 ? 'warning' : 'muted'}
          sub="Unpaid + grace + suspended (excludes new-order carts)."
          interactive={false}
        />
      </div>

      {/* Phase 10 — Services KPIs (T-10.1-13 + T-10.2-13).
          Shown as a separate band so the existing Phase 1 dashboard layout stays
          untouched and new readers immediately see "Services" as its own track. */}
      <div className="mt-2">
        <h2 className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <span className="text-primary">02/</span> Services & Care Plans (Phase 10)
        </h2>
        {phase10Error && (
          <NotificationCard
            tone="warning"
            title="Phase 10 metrics unavailable"
            body={phase10Error}
          />
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Care MRR (this month)"
            value={phase10 ? formatZAR(phase10.care_mrr_zar) : phase10Error ? '—' : '…'}
            icon={Heart}
            tone="success"
            sub={phase10 ? `${phase10.care_active_subs} active${phase10.care_at_risk_subs > 0 ? ` · ${phase10.care_at_risk_subs} at-risk` : ''}` : undefined}
            link={{ to: '/admin/care', label: 'Manage subscriptions' }}
            interactive={false}
          />
          <StatCard
            label="Care MRR — 12-month trend"
            value={
              phase10 ? (
                <CareMrrSparkline history={phase10.care_mrr_history} />
              ) : phase10Error ? '—' : '…'
            }
            icon={TrendingUp}
            tone="primary"
            sub="Read from v_admin_care_mrr_monthly. Read-through (AD-27)."
            interactive={false}
          />
          <StatCard
            label="Studio orders (lifetime)"
            value={phase10 ? phase10.studio_orders_lifetime.toString() : phase10Error ? '—' : '…'}
            icon={Sparkles}
            tone={phase10 && phase10.studio_orders_lifetime >= 5 ? 'success' : 'primary'}
            sub={
              phase10
                ? phase10.studio_orders_lifetime < 5
                  ? `${5 - phase10.studio_orders_lifetime} more before launch banner hides`
                  : 'Launch banner now hidden on /services/website-design'
                : undefined
            }
            interactive={false}
          />
          <StatCard
            label="Studio orders (this month)"
            value={phase10 ? phase10.studio_orders_this_month.toString() : phase10Error ? '—' : '…'}
            icon={Hammer}
            tone={phase10 && phase10.studio_in_progress > 0 ? 'info' : 'muted'}
            sub={phase10 ? `${phase10.studio_in_progress} in flight (consultation or build)` : undefined}
            interactive={false}
          />
          <StatCard
            label="Care attach rate"
            value={
              phase10
                ? phase10.care_attach_studio_count > 0
                  ? `${Math.round(phase10.care_attach_rate * 100)}%`
                  : '—'
                : phase10Error ? '—' : '…'
            }
            icon={Heart}
            tone={
              phase10 && phase10.care_attach_rate >= 0.3
                ? 'success'
                : phase10 && phase10.care_attach_studio_count > 0
                  ? 'warning'
                  : 'muted'
            }
            sub={
              phase10
                ? phase10.care_attach_studio_count > 0
                  ? `${phase10.care_attach_overlap}/${phase10.care_attach_studio_count} Studio customers · target 30%`
                  : 'No Studio customers yet'
                : undefined
            }
            link={{ to: '/admin/reports/services', label: 'Open services report' }}
            interactive={false}
          />
        </div>
      </div>

      <Card interactive={false} className="mt-2 border-info/30 bg-info/5 p-4 text-sm">
        <p className="text-foreground">
          Reports are read directly from <code>v_admin_dashboard_metrics</code> and the
          underlying tables — no caching, always live. Use the tabs above to drill into
          lifecycle stages, provisioning health, support SLA, and domain coverage.
        </p>
      </Card>
    </UniversalPage>
  );
}

/**
 * CareMrrSparkline — dependency-free SVG line chart of the last 12 months
 * of Care MRR. Resolves to a single horizontal line at zero before the
 * first paid subscription, then climbs as customers join. Inline + tiny
 * (no Recharts) keeps the bundle lean per the existing Reports Overview
 * pattern (KPI grid only, no chart library).
 */
function CareMrrSparkline({ history }: { history: Array<{ month_start: string; mrr_zar: number }> }) {
  if (history.length === 0) {
    return <span className="text-sm text-muted-foreground">No history yet</span>;
  }
  const max = Math.max(...history.map((p) => p.mrr_zar), 1);
  const w = 120;
  const h = 32;
  const stepX = w / Math.max(history.length - 1, 1);
  const points = history
    .map((p, i) => `${(i * stepX).toFixed(2)},${(h - (p.mrr_zar / max) * h).toFixed(2)}`)
    .join(' ');
  const lastPoint = history[history.length - 1];
  return (
    <div className="flex items-end gap-3">
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        className="text-primary"
        aria-label={`12-month Care MRR trend, peaking at R${max.toFixed(0)}`}
        role="img"
      >
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>
      <span className="text-sm font-semibold tabular-nums text-foreground">
        {formatZAR(lastPoint?.mrr_zar ?? 0)}
      </span>
    </div>
  );
}

